import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../config/api';

const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const { user, client } = useAuth();
  const [clientConfig, setClientConfig] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [availableClients, setAvailableClients] = useState([]);

  useEffect(() => {
    if (user) {
      initializeClient();
    } else {
      setClientConfig(null);
      setIsAdmin(false);
      setLoading(false);
    }
  }, [user, client]);

  // Effect para recargar la configuración del cliente cuando cambie
  useEffect(() => {
    if (client && !isAdmin) {
      loadClientConfigFromApi();
    }
  }, [client, isAdmin]);

  const initializeClient = async () => {
    try {
      setLoading(true);
      
      // Determinar si es admin
      const adminStatus = user?.role === 'admin';
      setIsAdmin(adminStatus);

      if (adminStatus) {
        // Para admins, cargar configuración por defecto pero también intentar cargar cliente específico si existe
        const defaultConfig = {
          id: 'admin',
          name: 'Administración',
          slug: 'admin',
          logo_url: '/amb-tu-logo-new.png',
          primary_color: '#0b1020',
          secondary_color: '#3b82f6',
          looker_dashboard_url: null, // Agregar campo para dashboard
          branding: {
            company_name: 'Amb Tu Platform',
            tagline: 'Panel de Administración',
            show_client_filter: true
          },
          features: {
            show_all_clients: true,
            show_client_switcher: true,
            show_global_analytics: true
          }
        };

        // Si hay un cliente específico, cargar su configuración también
        if (client) {
          const clientConfig = {
            id: client.id,
            name: client.name,
            slug: client.slug,
            logo_url: client.logo_url || '/amb-tu-logo-new.png',
            primary_color: client.primary_color || '#0b1020',
            secondary_color: client.secondary_color || '#3b82f6',
            looker_dashboard_url: client.looker_dashboard_url, // Agregar campo para dashboard
            branding: {
              company_name: client.name,
              tagline: `Dashboard de ${client.name}`,
              show_client_filter: false
            },
            features: {
              show_all_clients: false,
              show_client_switcher: false,
              show_global_analytics: false
            }
          };

          // Usar la configuración del cliente específico si tiene dashboard URL
          if (client.looker_dashboard_url) {
            setClientConfig(clientConfig);
          } else {
            setClientConfig(defaultConfig);
          }
        } else {
          setClientConfig(defaultConfig);
        }

        // Aplicar tema admin
        applyClientTheme({
          id: 'admin',
          name: 'Administración',
          slug: 'admin',
          logo_url: '/amb-tu-logo-new.png',
          primary_color: '#0b1020',
          secondary_color: '#3b82f6'
        });
      } else if (client) {
        // Para usuarios normales, cargar la configuración detallada desde la API
        await loadClientConfigFromApi();
      } else {
        // Usuario sin cliente asignado
        setClientConfig(null);
      }
    } catch (error) {
      console.error('Error initializing client:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClientConfigFromApi = async () => {
    try {
      const response = await fetch(`${API_URL}/api/client/config`, { credentials: 'include' });
      if (!response.ok) return;
      const apiConfig = await response.json();
      const parsedSettings = (() => {
        try {
          if (!apiConfig.settings) return {};
          return typeof apiConfig.settings === 'string' ? JSON.parse(apiConfig.settings) : apiConfig.settings;
        } catch (e) {
          console.warn('Error parsing settings from /api/client/config:', e);
          return {};
        }
      })();

      const config = {
        id: client?.id,
        name: apiConfig.name || client?.name,
        slug: apiConfig.slug || client?.slug,
        logo_url: apiConfig.logo_url || '/amb-tu-logo-new.png',
        primary_color: apiConfig.primary_color || '#0b1020',
        secondary_color: apiConfig.secondary_color || '#3b82f6',
        looker_dashboard_url: apiConfig.looker_dashboard_url || null,
        settings: parsedSettings,
        branding: {
          company_name: apiConfig.name || client?.name,
          tagline: `Dashboard de ${apiConfig.name || client?.name}`,
          show_client_filter: false
        },
        features: {
          show_all_clients: false,
          show_client_switcher: false,
          show_global_analytics: false
        }
      };

      // Mezclar ajustes de branding/features si están en settings
      if (parsedSettings?.branding) {
        config.branding = { ...config.branding, ...parsedSettings.branding };
      }
      if (parsedSettings?.features) {
        config.features = { ...config.features, ...parsedSettings.features };
      }

      setClientConfig(config);
      applyClientTheme(config);
    } catch (e) {
      console.error('Error loading client config from API:', e);
    }
  };

  const applyClientTheme = (config) => {
    const root = document.documentElement;
    
    // Aplicar colores CSS variables
    root.style.setProperty('--client-primary', config.primary_color);
    root.style.setProperty('--client-secondary', config.secondary_color);
    root.style.setProperty('--client-logo', `url(${config.logo_url})`);
    
    // Aplicar tema específico del cliente
    document.body.setAttribute('data-client', config.slug);
    document.body.setAttribute('data-client-id', config.id);
  };


  const getClientDisplayName = () => {
    if (isAdmin) {
      return 'Administración';
    }
    return clientConfig?.branding?.company_name || clientConfig?.name || 'Cliente';
  };

  const getClientLogo = () => {
    return clientConfig?.logo_url || '/amb-tu-logo-new.png';
  };

  const getClientColors = () => {
    return {
      primary: clientConfig?.primary_color || '#0b1020',
      secondary: clientConfig?.secondary_color || '#3b82f6'
    };
  };

  const getClientConfig = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, { credentials: 'include' });
      if (response.ok) {
        const clientData = await response.json();
        return {
          id: clientData.id,
          name: clientData.name,
          slug: clientData.slug,
          logo_url: clientData.logo_url || '/amb-tu-logo-new.png',
          primary_color: clientData.primary_color || '#0b1020',
          secondary_color: clientData.secondary_color || '#3b82f6',
          looker_dashboard_url: clientData.looker_dashboard_url,
          branding: {
            company_name: clientData.name,
            tagline: `Dashboard de ${clientData.name}`,
            show_client_filter: false
          },
          features: {
            show_all_clients: false,
            show_client_switcher: false,
            show_global_analytics: false
          }
        };
      }
    } catch (error) {
      console.error('Error fetching client config:', error);
    }
    return null;
  };

  const value = {
    clientConfig,
    isAdmin,
    loading,
    availableClients,
    getClientDisplayName,
    getClientLogo,
    getClientColors,
    getClientConfig,
    // Funciones de utilidad
    canAccessFeature: (feature) => {
      if (isAdmin) return true;
      return clientConfig?.features?.[feature] || false;
    },
    shouldShowClientFilter: () => {
      return clientConfig?.features?.show_client_filter || false;
    }
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
